/**
 * The demonstration tenant, and the selectors that turn it into figures.
 *
 * ── WHY THIS FILE GENERATES A COHORT INSTEAD OF LISTING NUMBERS ──────────
 * The panel this replaces held 468 lines of hand-typed fixtures, and the
 * numbers in them disagreed with each other: a funnel whose bars were drawn at
 * 68, 44 and 28 under labels reading 74%, 65% and 62%. A reader who checks two
 * figures against each other and finds they do not reconcile stops reading.
 *
 * So nothing here is a typed figure. This module builds a deterministic array
 * of student records and DERIVES every cell from it, through the real
 * suppression core in src/lib/suppression.ts. Three consequences:
 *
 *   1. The page cannot disagree with itself, or with an export, because both
 *      read the same derivation.
 *   2. The filters are genuinely wired. Filtering re-derives every figure,
 *      including the suppression, so a filter that pushes a group under ten
 *      makes that group disappear for real.
 *   3. The suppressed states in the demo are the states the code actually
 *      produces, not a designer's impression of them.
 *
 * ── THE INSTITUTION IS FICTIONAL ON PURPOSE ──────────────────────────────
 * The specs use Howard University in their worked examples. This build does
 * not. A screenshot of a real named university on a P3 dashboard escapes into
 * a deck within a week, and P3 has zero paying partners, so that screenshot
 * would be a claim P3 cannot support. Riverbend State University does not
 * exist. It is named as sample data in the header chip, in the rail, and in
 * Data and methods.
 *
 * ── EVERY FIGURE IS A `Cell` ─────────────────────────────────────────────
 * With exactly one specified exception: the progress meter in a threshold
 * state, which is `StateCard`'s own `progress` prop. A threshold state that
 * will not say how far you are from the threshold is not a threshold state,
 * the admin IA spec prints that number in both its worked examples, and the
 * foundation shipped the prop for it. It is never rendered as a metric.
 */

import type { BandId } from "~/app/lib/pricing";
import type { Provenance } from "~/app/components/primitives";
import {
  asOfStamp,
  formatDate,
  formatDateRange,
  formatMore,
  previousSunday,
} from "~/app/lib/format";
import {
  K,
  count,
  rate,
  suppressBuckets,
  suppressed,
  unavailable,
  value,
  type Cell,
  type Rate,
  type SuppressedBuckets,
} from "~/lib/suppression";
import { eventFigure } from "~/lib/event-figure";
import { ACTIVITY_WINDOW_DAYS, activityCells, activityWindow, type Ratio } from "~/lib/app-use";
import { questionCells, type QuestionRecord } from "~/lib/question-figures";
import { OUTCOME_MILESTONES } from "~/lib/outcome-milestones";
// The Worker's names for its two slices, so the sample's mentorship filter
// and a live slice document name the cut the same way (Sep 18 2026). A
// type-only module apart from this constant: nothing of the Worker's runtime
// comes with it.
import { SLICE_NAME } from "~/worker/lib/api-types";
// The sentence a live tenant reads where pathway views are not counted per
// institution (Sep 19 2026): a string, nothing of the Worker's runtime.
import { PATHWAYS_NOT_COUNTED } from "~/worker/lib/capabilities";
import {
  canonicalIndustry,
  fieldMatchCounts,
  INDUSTRIES as FIELD_INDUSTRIES,
  type FirstRole,
} from "~/lib/field-match";

/* ==========================================================================
   The clock

   A real timestamp from the data layer, never a string literal. The panel this
   replaces printed an "as of" that had been hardcoded five months earlier.
   ========================================================================== */

/** The demo snapshot instant. In production this is the cron's write time. */
export const AS_OF = "2026-09-07T16:12:00-05:00";

/** Recompute cadence, stated everywhere freshness is stated. */
export const CADENCE = "Recomputed hourly.";

/** The words "live" and "real-time" appear nowhere on this surface. */
export const FRESHNESS = {
  asOf: asOfStamp(AS_OF),
  cadence: CADENCE,
} as const;

/* ==========================================================================
   The tenant
   ========================================================================== */

export interface Term {
  readonly name: string;
  readonly start: string;
  readonly end: string;
  /** True when this is the institution's first term on P3, which is why the
   *  "Previous term" comparison is offered as unavailable rather than empty. */
  readonly isFirst: boolean;
}

/** The three roles, as the Worker and the security page name them. Owner:
 *  billing, members and data. Admin: members and data. Viewer: data only. */
export type TeamRole = "Owner" | "Admin" | "Viewer";

export interface Institution {
  readonly legalName: string;
  readonly displayName: string;
  readonly location: string;
  readonly joinSlug: string;
  /** Six characters. Not a magic link: university mail security pre-fetches
   *  inbound URLs and burns a one-time link before the human clicks it. */
  readonly joinCode: string;
  readonly joinUrl: string;
  /**
   * The email domain this institution's ADMIN TEAM signs in with, derived on
   * a live tenant from the signed-in member's own address (live-view.ts).
   *
   * It is NOT a verified domain, and it was called `verifiedDomain` until
   * Sep 12 2026, which made the dashboard tell every live tenant that its
   * domain had been verified and that students signing up with it would be
   * offered the institution. Nothing verifies a domain: `org_domains` is a
   * table in the architecture document and not in any migration, and no
   * student is offered anything. Renamed so the name cannot be read as a
   * capability. Used for the team roster's addresses and the invitation
   * placeholder, both of which are about admins and are correct.
   */
  readonly teamDomain: string;
  /** The capacity band the institution registered on. Its capacity is
   *  `seatsPurchased`; a test holds the two equal. A band is a price, not a
   *  meter: nothing is charged when more students claim the institution. */
  readonly capacityBand: BandId;
  readonly seatsPurchased: number;
  readonly planInterval: string;
  /** The day the subscription started: the start of the current period. */
  readonly subscribedAt: string;
  readonly renewsAt: string;
  readonly term: Term;
  /** Partner-supplied. The only denominator on this surface P3 did not
   *  compute, and it is labelled as theirs everywhere it is used. */
  readonly enrollmentEstimate: number | null;
  readonly viewerRole: TeamRole;
  readonly contactName: string;
}

/* Dates are written as instants in Central Time, not as bare calendar strings.
   A bare "2026-08-10" parses as UTC midnight, which formats as August 9 in
   Chicago, and a term that starts a day before it starts is exactly the kind of
   error a provost spots and a dashboard never recovers from. */
const TERM_FALL_2026: Term = {
  name: "Fall 2026",
  start: "2026-08-10T12:00:00-05:00",
  end: "2026-12-18T12:00:00-06:00",
  isFirst: true,
};

const BASE_INSTITUTION: Institution = {
  legalName: "Riverbend State University",
  displayName: "Riverbend State",
  location: "Baton Rouge, LA",
  joinSlug: "riverbend",
  joinCode: "RVRBND",
  joinUrl: "join.pulseofp3.org/riverbend",
  teamDomain: "riverbend.edu",
  capacityBand: "band_300",
  seatsPurchased: 300,
  planInterval: "Annual",
  subscribedAt: "2026-08-03T12:00:00-05:00",
  renewsAt: "2027-08-31T12:00:00-05:00",
  term: TERM_FALL_2026,
  enrollmentEstimate: null,
  viewerRole: "Owner",
  contactName: "Dean of Student Success",
};

/* ==========================================================================
   Scenarios

   Three, because these are the three screens that exist: what a prospect sees
   in a demo, what a real customer sees on the day they subscribe, and what
   they see once the product is doing the thing it was bought to do. Building
   only the third is how a dashboard ships that is unusable for its first six
   weeks.
   ========================================================================== */

export const SCENARIOS = [
  {
    id: "day-one",
    label: "Day one",
    caption: "0 students claimed",
    blurb:
      "The day the subscription starts. Nothing is measured yet, and the page has one job.",
  },
  {
    id: "first-weeks",
    label: "First weeks",
    caption: "12 students claimed",
    blurb:
      "Above the reporting floor at cohort level, below it almost everywhere inside.",
  },
  {
    id: "established",
    label: "Established",
    caption: "140 students claimed",
    blurb: "A cohort large enough that most breakdowns publish.",
  },
] as const;

export type ScenarioId = (typeof SCENARIOS)[number]["id"];
export const DEFAULT_SCENARIO: ScenarioId = "established";

export function isScenarioId(v: string): v is ScenarioId {
  return SCENARIOS.some((s) => s.id === v);
}

/** The demonstration cohort named in a query string, or the default. The
 *  settings screen reads it so it shows the same institution the dashboard
 *  was showing when the reader clicked through. */
export function scenarioFromSearch(search: string): ScenarioId {
  const params = new URLSearchParams(search);
  // `scenario` since Sep 17 2026; `cohort` names a cohort on both hosts now.
  // A link written before then still resolves: a scenario id can never spell
  // a cohort id, so the legacy value is read when it is one.
  const value = params.get("scenario") ?? params.get("cohort") ?? "";
  return isScenarioId(value) ? value : DEFAULT_SCENARIO;
}

/**
 * The sample institution's own cohorts (Sep 17 2026, Thomas: the cohort
 * filter on both hosts). The same two the sample Enrollment lists, so the
 * two screens agree. Each synthetic student is placed by index in
 * `buildCohort`, never by a draw, so adding them moved no published figure.
 */
export const DEMO_COHORTS: readonly { readonly id: string; readonly name: string }[] = [
  { id: "sample-c1", name: "College of Nursing" },
  { id: "sample-c2", name: "Fall 2026 orientation" },
  // Under the floor on purpose (Sep 19 2026), so the sample shows a cohort
  // that has no figures yet on both screens that name it.
  { id: "sample-c3", name: "Honors pilot" },
];

/** The sample cohort named in the query string, or null. */
export function demoCohortFromSearch(search: string): string | null {
  const value = new URLSearchParams(search).get("cohort") ?? "";
  return DEMO_COHORTS.some((c) => c.id === value) ? value : null;
}

/**
 * Roughly three in ten to the first cohort, four in ten to the second, six
 * students to the third, the rest in none. The third takes only students no
 * cohort held, so adding it moved no other cohort's figures.
 */
export function demoCohortFor(index: number): string | null {
  const slot = index % 10;
  if (slot < 3) return DEMO_COHORTS[0]!.id;
  if (slot < 7) return DEMO_COHORTS[1]!.id;
  if (slot === 7 && index < 60) return DEMO_COHORTS[2]!.id;
  return null;
}

/**
 * A cohort of the signed-in institution named in the query string (Sep 17
 * 2026): the same `cohort` parameter the sample host uses for its scenario,
 * telling the two apart by shape. A cohort id is `coh_` and 32 hex digits
 * and can never spell a scenario; a scenario can never spell an id.
 */
export function cohortIdFromSearch(search: string): string | null {
  const value = new URLSearchParams(search).get("cohort") ?? "";
  return /^coh_[0-9a-f]{32}$/.test(value) ? value : null;
}

interface ScenarioConfig {
  readonly claimed: number;
  readonly matched: number;
  /** Of the unmatched, how many have asked for a mentor and are still waiting.
   *  The admin IA spec calls this the most operationally useful figure on the
   *  page for a student-success office, and nothing surfaces it today. */
  readonly openRequests: number;
  readonly seatsPurchased: number;
  readonly enrollmentEstimate: number | null;
  /** First claim date, so the growth curve has an honest starting week. */
  readonly firstClaim: string;
  readonly subscribedAt: string;
}

const SCENARIO_CONFIG: Record<ScenarioId, ScenarioConfig> = {
  "day-one": {
    claimed: 0,
    matched: 0,
    openRequests: 0,
    seatsPurchased: 300,
    enrollmentEstimate: null,
    firstClaim: "2026-09-07",
    subscribedAt: "2026-09-07",
  },
  "first-weeks": {
    claimed: 12,
    matched: 5,
    openRequests: 4,
    seatsPurchased: 300,
    enrollmentEstimate: null,
    firstClaim: "2026-08-24",
    subscribedAt: "2026-08-21",
  },
  established: {
    claimed: 140,
    matched: 88,
    openRequests: 31,
    seatsPurchased: 300,
    enrollmentEstimate: 1200,
    firstClaim: "2026-08-10",
    subscribedAt: "2026-08-03",
  },
};

export function institutionFor(scenario: ScenarioId): Institution {
  const config = SCENARIO_CONFIG[scenario];
  return {
    ...BASE_INSTITUTION,
    seatsPurchased: config.seatsPurchased,
    enrollmentEstimate: config.enrollmentEstimate,
    subscribedAt: `${config.subscribedAt}T12:00:00-05:00`,
  };
}

/* ==========================================================================
   Controlled vocabularies

   These are P3's real picklists, not invented categories. Support areas and
   industries are the exact values the app writes to a student record.
   ========================================================================== */

export const SUPPORT_AREAS = [
  "Career guidance",
  "Networking opportunities",
  "Help with college applications",
  "Educational guidance",
  "Personal growth",
  "Motivation",
  "Someone to talk to about life",
  "Friendship",
  "Long term goals support",
  "Short term goals support",
] as const;

export const INDUSTRIES = [
  "Technology",
  "Business",
  "Health & Medicine",
  "Arts & Entertainment",
  "Education or Academia",
  "Law",
  "Science and Research",
  "Communications",
  "Community & Social Services",
  "Government",
  "Athletics",
  "Commerce & Retail",
  "Agriculture",
  "Other",
] as const;

export const EDUCATION_GOALS = [
  "Bachelor's degree",
  "Master's degree",
  "Associate degree",
  "Doctorate",
  "Trade or professional certification",
  "High school diploma",
  "Not sure yet",
] as const;

/* Ordered as a cadence, not by popularity, and rendered with `keepOrder` so the
   bars read as a scale. A frequency scale sorted by size is a chart a reader
   has to decode twice. */
export const MEETING_RHYTHMS = [
  "Weekly",
  "Every other week",
  "Monthly",
  "As needed",
] as const;

/**
 * How a student reached the roster. The first four are P3-owned join events,
 * so they are attributable exactly. The fifth is not: a student who picked the
 * institution from the in-app list left no join event, and the footnote on the
 * card says so rather than folding them into a channel they did not use.
 */
export const JOIN_CHANNELS = [
  "QR poster",
  "Advisor email",
  "Join link",
  "Verified email domain",
  "Selected your institution in the app",
] as const;

/**
 * The Mentee Milestone Framework names, exactly as they arrive in P3's
 * analytics (`milestoneName` on MilestoneCompleted). Real names, in the
 * pathway's own order, so a "top milestones" list on the demo ranks the same
 * vocabulary a live tenant's will.
 */
export const MILESTONES = [
  "Identify Career Curiosities",
  "Started High School",
  "Achieved Academic Milestone",
  "Joined a Campus or Community Organization",
  "Started a Leadership Role",
  "Earned My First Income or Volunteer Hours",
  "Started College or Post-Secondary Program",
  "Declared My Major/Focus",
  "Started Working While Studying",
  "Completed an Internship or Research Experience",
  "Landed My First Full-Time Role",
] as const;

/**
 * The milestones that carry an outcome: the app requires the student to say
 * where (which program, which major, which employer) before one of these
 * completes. Student reported and unverified, and labelled that way on every
 * surface that shows them.
 */
/** The progress buckets, in order. */
const PROGRESS_ORDER = ["None", "One", "Two", "Three or more"] as const;

export { OUTCOME_MILESTONES };

/** The seventeen career pathways in the app's guide. Nine from the corpus,
 *  eight authored; the count is the one /pricing states. */
export const PATHWAYS = [
  "Tech",
  "Healthcare",
  "Business",
  "Banking & Finance",
  "IT / Engineering",
  "Legal",
  "Education",
  "Executive Leadership",
  "Sports & Athletics",
  "Communications",
  "Science",
  "Government",
  "Human Resources",
  "Arts",
  "Community",
  "Agriculture",
  "Trades",
] as const;

/* ==========================================================================
   The generator

   mulberry32, seeded per scenario, so a reload, a filter change and a rebuild
   all produce the same cohort. A demo whose numbers move between reloads is a
   demo a buyer stops believing.
   ========================================================================== */

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** One weighted draw. Weights are relative and need not sum to anything. */
function weighted<T>(
  rng: () => number,
  options: readonly T[],
  weights: readonly number[],
): T {
  const first = options[0];
  if (first === undefined) throw new Error("weighted() needs at least one option");
  let total = 0;
  for (let i = 0; i < options.length; i++) total += weights[i] ?? 1;
  let roll = rng() * total;
  for (let i = 0; i < options.length; i++) {
    roll -= weights[i] ?? 1;
    if (roll <= 0) return options[i] ?? first;
  }
  return options[options.length - 1] ?? first;
}

const DAY_MS = 86_400_000;

export interface DemoStudent {
  readonly id: string;
  /** The date the student attached to this institution. */
  readonly claimedAt: string;
  /** Measured on the student's own record, never derived by subtracting
   *  mentor-side counts: the Data Matrix documents a derived figure reading
   *  427 where the truth was 550, because students hold more than one. */
  readonly mentorships: number;
  /** An open request means the student asked for a mentor and is not matched. */
  readonly openRequest: boolean;
  readonly supportAreas: readonly string[];
  readonly industry: string;
  readonly educationGoal: string;
  readonly meetingRhythm: string;
  readonly channel: string;
  /** The sample cohort this student is in, by index (Sep 17 2026), or none. */
  readonly cohort: string | null;
  /** 18 or over. Pulse AI and CV review are offered to adult students only. */
  readonly adult: boolean;
  /** Milestones the student marked complete, each dated. */
  readonly milestones: readonly MilestoneCompletion[];
  readonly opportunitiesOpened: number;
  readonly opportunitiesApplied: number;
  readonly questionsAsked: number;
  /** Zero for a student under 18, by construction, not by filtering. */
  readonly pulseSessions: number;
  readonly pathwaysExplored: readonly string[];
  /** Running totals on the student's record (Sep 11 2026): mentor video
   *  plays, videos saved, mentors followed. Drawn from their own stream after
   *  matching, so adding them moved no figure the sample already published. */
  readonly videoPlays: number;
  readonly videosSaved: number;
  readonly mentorsFollowed: number;
  /** App use in the 90 days before the sample's date (Sep 11 2026): sessions,
   *  and their total length in seconds. Drawn from a fourth stream, last, so
   *  adding them moved no figure the sample already published. */
  readonly appSessions: number;
  readonly appSeconds: number;
  /** Each question the student asked, dated, answered or not: as many as `questionsAsked`. */
  readonly questionLog: readonly { readonly at: string; readonly answered: boolean }[];
  /** The student's Career Timeline (Sep 12 2026): each entry's kind and stage,
   *  never its text. Drawn from a fifth stream, last, so adding it moved no
   *  figure the sample already published. */
  readonly timeline: readonly TimelineEntry[];
}

/** One Career Timeline entry, as the web store counts it: a school or a role,
 *  in the past, now, or planned. The sample holds no title or place. */
export interface TimelineEntry {
  readonly kind: "education" | "role";
  readonly stage: "past" | "current" | "future";
}

export interface MilestoneCompletion {
  readonly name: string;
  /** Calendar date, Central Time. */
  readonly at: string;
}

/** Deterministic, and exact on the two headline counts by construction. */
export function buildCohort(scenario: ScenarioId): readonly DemoStudent[] {
  const config = SCENARIO_CONFIG[scenario];
  if (config.claimed === 0) return [];

  const rng = mulberry32(
    scenario === "established" ? 20260907 : scenario === "first-weeks" ? 771 : 3,
  );
  // Activity draws from its own stream so adding it in Sep 2026 did not move a
  // single figure the cohort already published.
  const rngActivity = mulberry32(
    scenario === "established" ? 4451 : scenario === "first-weeks" ? 912 : 5,
  );
  /* A THIRD STREAM, for the same reason the second one exists (Sep 12 2026).
     The first-role milestone is drawn here and nowhere else, so adding it
     moved no figure the cohort already published: MILESTONE_WEIGHTS gives it
     a weight of 1, mirroring the real platform where exactly one student has
     completed it, and raising that weight would have shifted every draw after
     it in rngActivity and with it every milestone, progress and activity
     number in the sample. See firstRoleFor(). */
  const rngFirstRole = mulberry32(
    scenario === "established" ? 8821 : scenario === "first-weeks" ? 331 : 7,
  );
  const first = Date.parse(`${config.firstClaim}T12:00:00-05:00`);
  const last = Date.parse("2026-09-06T12:00:00-05:00");
  const span = Math.max(last - first, DAY_MS);

  const students: DemoStudent[] = [];
  for (let i = 0; i < config.claimed; i++) {
    // Front-loaded arrival: a launch push, then a long tail. Squaring the draw
    // pulls dates toward the start, which is the shape a term-start campaign
    // actually produces.
    const t = rng() ** 1.7;
    const claimedAt = new Date(first + t * span).toISOString().slice(0, 10);

    const areaCount = weighted(rng, [1, 2, 3, 4], [22, 34, 28, 16]);
    const areas = new Set<string>();
    while (areas.size < areaCount) {
      areas.add(
        weighted(
          rng,
          SUPPORT_AREAS,
          [46, 29, 21, 17, 14, 11, 8, 6, 5, 4],
        ),
      );
    }

    students.push({
      id: `s${String(i + 1).padStart(4, "0")}`,
      claimedAt,
      mentorships: 0,
      openRequest: false,
      supportAreas: [...areas],
      industry: weighted(
        rng,
        INDUSTRIES,
        [23, 19, 17, 11, 9, 8, 7, 6, 6, 5, 4, 4, 3, 5],
      ),
      educationGoal: weighted(rng, EDUCATION_GOALS, [56, 19, 11, 8, 6, 5, 7]),
      meetingRhythm: weighted(rng, MEETING_RHYTHMS, [20, 38, 31, 11]),
      channel: weighted(rng, JOIN_CHANNELS, [30, 26, 18, 9, 17]),
      // By index, not by a draw: no stream moves, no published figure moves.
      cohort: demoCohortFor(i),
      ...activityFor(rngActivity, claimedAt, rngFirstRole),
      // Filled from their own streams after matching, below.
      videoPlays: 0,
      videosSaved: 0,
      mentorsFollowed: 0,
      appSessions: 0,
      appSeconds: 0,
      questionLog: [],
      timeline: [],
    });
  }

  // Matching is assigned to an exact count rather than sampled, so the headline
  // rate is the rate the scenario claims. A student who matched holds one
  // mentorship, and roughly one in five holds two: the platform total is
  // higher than the number of matched students and the copy says why.
  const order = students.map((_, i) => i);
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const a = order[i];
    const b = order[j];
    if (a === undefined || b === undefined) continue;
    order[i] = b;
    order[j] = a;
  }

  const out = students.map((s) => ({ ...s }));
  for (let k = 0; k < config.matched; k++) {
    const idx = order[k];
    if (idx === undefined) continue;
    const target = out[idx];
    if (!target) continue;
    target.mentorships = rng() < 0.19 ? 2 : 1;
  }
  for (let k = 0; k < config.openRequests; k++) {
    const idx = order[config.matched + k];
    if (idx === undefined) continue;
    const target = out[idx];
    if (!target) continue;
    target.openRequest = true;
  }

  // The record totals, drawn last and from their own stream, so adding them
  // (Sep 11 2026) moved no figure the sample already published.
  const rngRecords = mulberry32(
    scenario === "established" ? 8123 : scenario === "first-weeks" ? 1337 : 7,
  );
  // App use and each question's date and answer (Sep 11 2026), drawn last and
  // from a fourth stream, so adding them moved no figure already published.
  const rngApp = mulberry32(
    scenario === "established" ? 3301 : scenario === "first-weeks" ? 4242 : 11,
  );
  // Career Timelines (Sep 12 2026), from a fifth stream for the same reason.
  const rngTimeline = mulberry32(
    scenario === "established" ? 6607 : scenario === "first-weeks" ? 5519 : 13,
  );
  return out.map((s) => {
    const records = recordsFor(rngRecords, s.mentorships > 0 || s.openRequest);
    return {
      ...s,
      ...records,
      ...appFor(rngApp, s.claimedAt, s.questionsAsked, s.mentorships > 0 || s.openRequest || records.videoPlays > 0),
      timeline: timelineFor(rngTimeline),
    };
  });
}

/**
 * A sample student's Career Timeline.
 *
 * PLACEHOLDER shares, chosen to show the card working (Thomas, Sep 12 2026:
 * placeholder numbers are fine in the demo). About two in five students keep
 * one; a school is most often current and a role most often planned, which
 * is the shape a timeline a student fills in for themselves takes.
 */
function timelineFor(rng: () => number): readonly TimelineEntry[] {
  if (rng() >= 0.42) return [];
  const size = weighted(rng, [1, 2, 3, 4, 5], [30, 28, 20, 13, 9]);
  return Array.from({ length: size }, (): TimelineEntry => {
    const kind = rng() < 0.55 ? "education" : "role";
    const stage = weighted(
      rng,
      ["past", "current", "future"] as const,
      kind === "education" ? [34, 46, 20] : [30, 22, 48],
    );
    return { kind, stage };
  });
}

/* ==========================================================================
   Filters

   Every control is wired to a named field or it does not ship. There is no
   region filter, no major filter, no class-year filter and no engagement tier,
   because none of those fields exists on a P3 student record. Data and methods
   names them.
   ========================================================================== */

export const WINDOWS = [
  { id: "term", label: "This term" },
  { id: "90d", label: "Last 90 days" },
  { id: "12m", label: "Last 12 months" },
  { id: "all", label: "All time" },
] as const;

export type WindowId = (typeof WINDOWS)[number]["id"];

export const MENTORSHIP_FILTERS = [
  { id: "any", label: "All students" },
  { id: "matched", label: "Matched with a mentor" },
  { id: "waiting", label: "Awaiting mentorship" },
] as const;

export type MentorshipFilterId = (typeof MENTORSHIP_FILTERS)[number]["id"];

export const COMPARISONS = [
  { id: "none", label: "Nothing" },
  { id: "prev", label: "Previous term" },
  { id: "platform", label: "All P3 students" },
] as const;

export type ComparisonId = (typeof COMPARISONS)[number]["id"];

export interface CohortFilters {
  readonly window: WindowId;
  readonly mentorship: MentorshipFilterId;
  readonly compare: ComparisonId;
  /** One of the institution's own cohorts (Sep 17 2026), or the whole
   *  institution. A scope rather than a filter: "Clear filters" leaves it. */
  readonly cohort: string | null;
}

/* The support-area filter left on Sep 17 2026 (Thomas): the cohort select took
   its place on both hosts, and Mentee Goals still shows the areas as a group. */

export const DEFAULT_FILTERS: CohortFilters = {
  window: "term",
  mentorship: "any",
  compare: "none",
  cohort: null,
};

export function filtersAreDefault(f: CohortFilters): boolean {
  return f.window === DEFAULT_FILTERS.window && f.mentorship === DEFAULT_FILTERS.mentorship;
}

/** Everything a chip row needs, including the removal that actually removes. */
export function activeFilterChips(
  f: CohortFilters,
): readonly { key: keyof CohortFilters; label: string; clearTo: CohortFilters }[] {
  const chips: { key: keyof CohortFilters; label: string; clearTo: CohortFilters }[] =
    [];
  if (f.window !== "term") {
    const w = WINDOWS.find((x) => x.id === f.window);
    chips.push({
      key: "window",
      label: w ? w.label : f.window,
      clearTo: { ...f, window: "term" },
    });
  }
  if (f.mentorship !== "any") {
    const m = MENTORSHIP_FILTERS.find((x) => x.id === f.mentorship);
    chips.push({
      key: "mentorship",
      label: m ? m.label : f.mentorship,
      clearTo: { ...f, mentorship: "any" },
    });
  }
  return chips;
}

function windowStart(w: WindowId, institution: Institution): number {
  const now = Date.parse(AS_OF);
  switch (w) {
    case "term":
      return Date.parse(institution.term.start);
    case "90d":
      return now - 90 * DAY_MS;
    case "12m":
      return now - 365 * DAY_MS;
    case "all":
      return Number.NEGATIVE_INFINITY;
  }
}

export function windowLabel(w: WindowId, institution: Institution): string {
  if (w === "term") {
    return `${institution.term.name}, ${formatDateRange(
      institution.term.start,
      institution.term.end,
    )}`;
  }
  if (w === "all") return "Every student who has ever claimed your institution";
  const start = windowStart(w, institution);
  return formatDateRange(start, AS_OF);
}

/**
 * What a window spans, finishing "joined ..." or "marked complete ..." on a
 * tile (Sep 18 2026, Thomas asked "what window?" of the joined tile). The
 * term by its name, because the term window runs from the term's start to
 * today; a trailing period by its length; "to date" when there is no window.
 */
export function windowSpan(w: WindowId, institution: Institution): string {
  switch (w) {
    case "term":
      return `in ${institution.term.name}`;
    case "90d":
      return "in the last 90 days";
    case "12m":
      return "in the last 12 months";
    case "all":
      return "to date";
  }
}

function applyFilters(
  students: readonly DemoStudent[],
  filters: CohortFilters,
  institution: Institution,
): readonly DemoStudent[] {
  const from = windowStart(filters.window, institution);
  return students.filter((s) => {
    if (Date.parse(`${s.claimedAt}T12:00:00-05:00`) < from) return false;
    if (filters.cohort && s.cohort !== filters.cohort) return false;
    if (filters.mentorship === "matched" && s.mentorships === 0) return false;
    if (filters.mentorship === "waiting" && s.mentorships > 0) return false;
    return true;
  });
}

/* ==========================================================================
   The platform comparator

   The one comparison P3 can compute both sides of. Verified by direct
   read-only query on 2026-09-07 across all 955 student records: unaffiliated
   students, meaning those who chose "Other" or left the field blank, hold a
   mentorship at 259 of 668. Institution-affiliated cohorts sat at 78%, 68% and
   62% on the same read.

   It is a comparison between groups. It is never a measure of what a
   partnership caused, and the insight line says so in those words.
   ========================================================================== */

export const PLATFORM = {
  matchedNumerator: 259,
  matchedDenominator: 668,
  verifiedOn: "2026-09-07T12:00:00-05:00",
  /** P3 staff accounts are excluded so they cannot inflate the baseline. */
  exclusion:
    "The comparison group is students who have not named an institution. P3's own staff accounts are excluded from it.",
} as const;

export const platformMatchedRate: Cell<Rate> = rate(
  PLATFORM.matchedNumerator,
  PLATFORM.matchedDenominator,
);

/* ==========================================================================
   Derived shapes
   ========================================================================== */

export interface DistRow {
  readonly label: string;
  readonly cell: Cell<number>;
  /** Bar length as a fraction of the largest published row, or null when the
   *  row has no publishable length. A LENGTH IS A CLAIM: a suppressed row is
   *  never drawn at a plausible width. */
  readonly length: number | null;
  readonly isRemainder?: boolean;
}

export interface Distribution {
  readonly key: string;
  readonly rows: readonly DistRow[];
  readonly remainder: DistRow | null;
  readonly hiddenCount: number;
  readonly total: Cell<number>;
  /**
   * True when the breakdown has nothing to say: either no row survived
   * suppression, or every surviving row is a zero sitting above a remainder
   * that holds the whole cohort. The second case is the one that is easy to
   * miss. Zero is a publishable measurement, so `suppressBuckets` keeps a zero
   * bucket, and a card can end up listing three categories nobody chose above a
   * combined row holding everyone. That reads as a broken chart. It takes the
   * threshold state instead, which reads as a measurement that has not started.
   */
  readonly allSuppressed: boolean;
  readonly provenance: Provenance;
  readonly footnote: string;
  /** Names what the chart's axis counts, for the accessible table. */
  readonly unit: string;
}

export interface GrowthPoint {
  readonly weekEnding: string;
  readonly label: string;
  readonly cumulative: Cell<number>;
  readonly added: Cell<number>;
}

export interface GrowthSeries {
  readonly points: readonly GrowthPoint[];
  /** Only the weeks whose cumulative figure publishes. The path is drawn from
   *  these and from nothing else. */
  readonly drawable: readonly { label: string; value: number }[];
  readonly suppressedWeeks: number;
  readonly annotations: readonly Annotation[];
  readonly provenance: Provenance;
}

/** Partner-written. The only way P3 can help a partner ask "did what we did
 *  change anything" without P3 making a causal claim: the partner marks their
 *  own intervention and the eye does the correlation. */
export interface Annotation {
  readonly date: string;
  readonly label: string;
}

export interface SeatMeter {
  readonly purchased: number;
  readonly claimed: Cell<number>;
  readonly claimedRaw: number;
  readonly available: Cell<number>;
  readonly fraction: number;
  readonly overCapacity: boolean;
  readonly renewsAt: string;
  readonly visibleToViewer: boolean;
}

export interface Coverage {
  readonly available: boolean;
  readonly estimate: number | null;
  readonly claimed: Cell<number>;
  readonly rate: Cell<Rate>;
  readonly provenance: Provenance;
}

/**
 * One kind of activity, as a total and as the group of students behind it.
 *
 * A total is a count of events by a group of students, so it publishes only
 * when that group clears the floor: ten applications by four students is a
 * figure about four people. `share` is the group over the students in view.
 */
export interface ActivityFigure {
  readonly total: Cell<number>;
  readonly students: Cell<number>;
  /** Only ever used to drive a threshold meter, never rendered as a figure. */
  readonly studentsRaw: number;
  readonly share: Cell<Rate>;
}

export interface Activity {
  readonly opportunitiesOpened: ActivityFigure;
  readonly opportunitiesApplied: ActivityFigure;
  /** Students who applied over students who opened one. */
  readonly applyRate: Cell<Rate>;
  readonly questions: ActivityFigure;
  /** Denominated on adult students: Pulse AI is not offered under 18. */
  readonly pulse: ActivityFigure;
  readonly adults: Cell<number>;
  readonly adultsRaw: number;
  readonly pathways: ActivityFigure;
  readonly pathwaysExplored: Distribution;
  readonly provenance: Provenance;
  readonly questionsProvenance: Provenance;
  readonly pulseProvenance: Provenance;
  readonly pathwaysProvenance: Provenance;
  /** Running totals from student records (Sep 11 2026): mentor video plays,
   *  videos saved, mentors followed. Measured on a live tenant. */
  readonly videoPlays: ActivityFigure;
  readonly videosSaved: ActivityFigure;
  readonly mentorsFollowed: ActivityFigure;
  /** Students with no question, video play, save, follow, mentorship or open
   *  request on record. The count publishes only when its share does. */
  readonly notYetActive: Cell<number>;
  readonly notYetActiveShare: Cell<Rate>;
  /** Only ever used to drive a threshold meter. On a live tenant it is the
   *  published value or zero: the Worker sends no count under the floor. */
  readonly notYetActiveRaw: number;
  /** False only on a live tenant whose record totals are not computed yet
   *  (no snapshot, or one written before they existed). */
  readonly recordsMeasured: boolean;
  readonly videosProvenance: Provenance;
  readonly notYetActiveProvenance: Provenance;
  /**
   * Set on a live tenant while P3 does not count app activity per
   * institution (the snapshot's `appActivity` source is notMeasured: the
   * analytics are not configured, or the snapshot predates Sep 11 2026). The
   * sentence is the source's own reason; the panel renders it in one card in
   * place of Opportunities, app use, Pulse AI and pathways. Never set on the
   * sample, which demonstrates the cards.
   */
  readonly notMeasured?: string;
  /** App use in the trailing 90 days, from the app's analytics (Sep 11 2026). */
  readonly appUse: AppUse;
  /** Questions asked in the trailing 90 days, the share answered, and a weekly running total (Sep 11 2026). */
  readonly answers: Answers;
  /**
   * Set on a live tenant while Pulse AI is not counted per institution: the
   * snapshot's own sentence, in one card beside app use. Cleared once Pipe D
   * reads the web store, when the Pulse card draws real figures. Never set on
   * the sample.
   *
   * Until Sep 12 2026 this one field ALSO decided whether the tenant was live
   * and whether the Pathways section showed. That coupling meant counting
   * Pulse would have put a Pathways card on every live tenant for a figure P3
   * does not count, so each job now has its own field below.
   */
  readonly pulseNotMeasured?: string;
  /** Set on a live tenant while pathway views are not counted per
   *  institution; hides the Pathways section. Never set on the sample. */
  readonly pathwaysNotMeasured?: string;
  /** A live tenant whose app activity is counted from the analytics, which
   *  counts applications and not opens. Drives the Opportunities card's
   *  live wording. Never set on the sample. */
  readonly liveTenant?: true;
}

/**
 * App use over the trailing 90 days (Sep 11 2026), from the app's analytics:
 * the students active, sessions per student and the average session length.
 * A student counts only when their analytics profile names the institution,
 * so every figure is a floor. Built by `src/lib/app-use.ts` on both hosts.
 */
export interface AppUse {
  readonly active: Cell<number>;
  /** Only ever used to drive a threshold meter; the published value or zero on a live tenant. */
  readonly activeRaw: number;
  readonly activeShare: Cell<Rate>;
  readonly sessionsPerStudent: Cell<Ratio>;
  readonly averageSessionSeconds: Cell<Ratio>;
  /** Finishes "active ...": "in the last 90 days". */
  readonly span: string;
  readonly provenance: Provenance;
  /** Set on a live tenant when the last refresh could not read the analytics: the source's own sentence. */
  readonly unread?: string;
}

/**
 * Questions from the question records (Sep 11 2026): asked in the trailing
 * 90 days, the share of every question a mentor answered, and a weekly
 * running total. Built by `src/lib/question-figures.ts` on both hosts.
 */
export interface Answers {
  readonly asked: ActivityFigure;
  readonly answered: Cell<Rate>;
  readonly weekly: readonly { readonly weekEnding: string; readonly cumulative: Cell<number> }[];
  /** Finishes "asked ...": "in the last 90 days". */
  readonly span: string;
  readonly provenance: Provenance;
  /** Set on a live tenant when the last refresh could not read the question records. */
  readonly unread?: string;
  /** Set on a live tenant whose snapshot predates the question records. */
  readonly notMeasured?: string;
}

export interface Milestones {
  readonly completed: Cell<number>;
  readonly completedRaw: number;
  /** Students who completed at least one milestone in the window. */
  readonly students: Cell<number>;
  readonly studentsRaw: number;
  readonly rate: Cell<Rate>;
  /** Completions per completing student, one decimal. */
  readonly perStudent: Cell<number>;
  readonly top: Distribution;
  readonly byWeek: GrowthSeries;
  /** The four outcome milestones IN THE ORDER A STUDENT REACHES THEM (a
   *  program started, a major declared, an internship, work), each row the
   *  distinct students who reported it. A funnel of stages, never of
   *  conversions: the stages are not nested and no rate is drawn between them. */
  readonly outcomes: Distribution;
  /** Every student in view by milestones completed in the window: none,
   *  one, two, three or more. Progress, with no causal frame. */
  readonly progress: Distribution;
  readonly outcomeStudents: Cell<number>;
  readonly outcomeStudentsRaw: number;
  /** Of the students whose first full-time role can be compared with the
   *  industry on their own profile, the share working in that field. The
   *  denominator is those comparable students, not the cohort. Unavailable
   *  until a cohort has ten of them, which is the usual reading. */
  readonly fieldMatch: Cell<Rate>;
  readonly provenance: Provenance;
  readonly outcomesProvenance: Provenance;
  /** What "marked complete" spans, finishing the sentence: the sample's
   *  window by name ("in Fall 2026", from `windowSpan`); "since each student
   *  joined P3" on a live tenant, whose figures are the students' records
   *  rather than a window (Sep 11 2026). */
  readonly span: string;
  /** As on Activity: the source's reason while milestones are not collected per institution. */
  readonly notMeasured?: string;
  /** Set on a live tenant when the last refresh could not read the milestone
   *  records (the source is unavailable). The sentence is the source's own. */
  readonly unread?: string;
}

/**
 * Career Timelines (Sep 12 2026, Pipe D): the students who keep one, their
 * entries, and those entries by kind and by stage. Counts from the web store,
 * never an entry's text. Built on both hosts under the store's rules: a count
 * of entries is a figure about the students behind it, and a group of buckets
 * publishes whole or not at all (p3-web-store `groupOrNothing`).
 */
export interface CareerTimelines {
  /** Students with at least one entry, over the students in view. */
  readonly withTimeline: Cell<Rate>;
  /** Only ever used to drive a threshold meter; the published value or zero on a live tenant. */
  readonly studentsRaw: number;
  /** Entries, with the students behind them. */
  readonly entries: ActivityFigure;
  /** Education and roles. */
  readonly byKind: Distribution;
  /** Past, current and planned. */
  readonly byStage: Distribution;
  readonly provenance: Provenance;
  /** Set on a live tenant while timelines are not counted per institution: the source's own sentence. */
  readonly notMeasured?: string;
  /** Set on a live tenant when the last refresh could not read the web store. */
  readonly unread?: string;
}

/** The store's names for a timeline entry's kind and stage, as the card says them. */
export const TIMELINE_KIND_LABELS: Readonly<Record<TimelineEntry["kind"], string>> = {
  education: "Education",
  role: "Roles",
};
export const TIMELINE_STAGE_LABELS: Readonly<Record<TimelineEntry["stage"], string>> = {
  past: "Past",
  current: "Current",
  future: "Planned",
};
export const TIMELINE_KINDS = ["education", "role"] as const;
export const TIMELINE_STAGES = ["past", "current", "future"] as const;

/** What a Career Timeline figure is, word for word on both hosts. */
export const TIMELINE_DEFINITION =
  "The schools and roles your students add to their own Career Timeline in P3, past, current or planned.";
export const TIMELINE_LIMITS: readonly string[] = [
  "Added by the student and not verified. P3 holds no registrar, enrollment or employer record.",
  "Only counts reach this page, never an entry's school, role or dates.",
  "An entry the student deleted is not counted.",
];

export interface CohortView {
  readonly scenario: ScenarioId;
  readonly institution: Institution;
  readonly filters: CohortFilters;
  readonly filtersActive: boolean;
  readonly windowLabel: string;

  /** The cohort total before the mentorship and support-area filters, used to
   *  decide whether the whole page is above the reporting floor. */
  readonly cohortSize: number;
  readonly belowFloor: boolean;

  readonly claimed: Cell<number>;
  readonly claimedInView: number;
  readonly claimedProvenance: Provenance;

  readonly growth: GrowthSeries;
  readonly seat: SeatMeter;
  readonly coverage: Coverage;
  readonly channels: Distribution;

  readonly matchedCount: Cell<number>;
  readonly matchedRate: Cell<Rate>;
  readonly mentorshipsHeld: Cell<number>;
  readonly perMatched: Cell<number>;
  readonly matchedProvenance: Provenance;

  readonly waitingTotal: Cell<number>;
  readonly waitingWithRequest: Cell<number>;
  readonly waitingWithoutRequest: Cell<number>;
  readonly waitingRaw: number;
  readonly waitingProvenance: Provenance;
  readonly mentorshipCounts: Distribution;

  readonly supportAreas: Distribution;
  readonly industries: Distribution;
  readonly educationGoals: Distribution;
  readonly meetingRhythms: Distribution;

  readonly activity: Activity;
  readonly milestones: Milestones;
  readonly timelines: CareerTimelines;

  readonly comparisonAvailable: Record<ComparisonId, { ok: boolean; reason?: string }>;

  /** The one comparison P3 computes both sides of. */
  readonly platform: PlatformComparison;
  /** The instant the figures were computed, ISO. The hero's stamp and the export's file name. */
  readonly asOf: string;
  /** The hero's freshness line after the term name: the stamp and the cadence,
   *  or the fact that nothing has been computed yet. Never "live". */
  readonly freshnessLine: string;
  /** True when the figures are older than the cadence promises (the Worker's
   *  three-hour rule). Always false on the sample. */
  readonly stale: boolean;
  /** A cut's figures the tick kept rather than replace (Sep 19 2026). Never on the sample. */
  readonly held: boolean;
  /** The cohort these figures are for (Sep 17 2026), by the name the
   *  institution gave it. Absent for the whole institution, and on the sample. */
  readonly cohortLabel?: string;
  /** The slice these figures are for (Sep 18 2026), by the Worker's name for
   *  it: the students with a mentor, or those waiting for one. The sample's
   *  mentorship filter is the same cut and carries the same name. Absent for
   *  the whole institution and under a cohort, which never pairs with a slice. */
  readonly sliceLabel?: string;
}

/**
 * The comparison group and how to name it. On the sample it is the verified
 * constant above (students who have not named an institution, read on Sep 7
 * 2026); on a live tenant it is every student on P3 from the same index pull
 * as the tenant's own figures. The panel reads the words from here so the
 * two are never described in each other's terms.
 */
export interface PlatformComparison {
  readonly matched: Cell<Rate>;
  /** The chart row's name. "Students with no institution". */
  readonly label: string;
  /** The group in a sentence. "students who have not named an institution". */
  readonly group: string;
  /** Where and when the figure was read, for the chart row's second line. */
  readonly note: string;
  /** The footnote shown while the comparison is drawn, or null. */
  readonly footnote: string | null;
}

/* ==========================================================================
   Distribution builder

   suppressBuckets() does the contractual work: it hides every bucket under
   ten, then keeps absorbing the next-smallest published bucket until the
   hidden set is both plural and ambiguous, so a hidden value cannot be
   recovered by subtracting the rest from the total. This function only dresses
   the result for the screen.
   ========================================================================== */

export interface DistOptions {
  readonly key: string;
  readonly unit: string;
  readonly footnote: string;
  readonly provenance: Provenance;
  readonly cap?: number;
  /** Preserves a meaningful order (a taxonomy the reader scans in sequence).
   *  Off by default, so lists rank. */
  readonly keepOrder?: boolean;
  /**
   * Off for a distribution that is not counting students. The suppression
   * legend is verbatim contractual copy that names students, and appending it
   * to a chart of mentors would state the commitment about the wrong people.
   * Those cards carry their own sentence instead.
   */
  readonly studentLegend?: boolean;
}

function buildDistribution(
  buckets: readonly { label: string; n: number }[],
  options: DistOptions,
): Distribution {
  return dressDistribution(suppressBuckets(buckets), options);
}

/**
 * The screen dressing alone, over a result the suppression core has already
 * produced. Exported (Sep 10 2026) for the live dashboard, whose breakdowns
 * arrive from the Worker already suppressed: the app never sees a raw bucket
 * and never runs the core a second time.
 */
export function dressDistribution(
  result: SuppressedBuckets,
  options: DistOptions,
): Distribution {
  const ordered = options.keepOrder
    ? [...result.shown]
    : [...result.shown].sort((a, b) => b.n - a.n);
  const capped =
    options.cap && ordered.length > options.cap
      ? ordered.slice(0, options.cap)
      : ordered;

  const peak = capped.reduce((m, b) => Math.max(m, b.n), 0);
  const rows: DistRow[] = capped.map((b) => ({
    label: b.label,
    cell: count(b.n),
    length: peak > 0 ? b.n / peak : null,
  }));

  // Rows past the cap are published figures, so they are folded into their own
  // computed line rather than into the privacy remainder. Conflating "too many
  // to list" with "too few to publish" is the error the whole surface avoids.
  const overflow = ordered.length - capped.length;
  if (overflow > 0) {
    const overflowSum = ordered.slice(capped.length).reduce((s, b) => s + b.n, 0);
    rows.push({
      // "and 2 more groups", never "2 groups with fewer than 10": one is a
      // list that was capped for length, the other is a privacy remainder, and
      // reading them as the same thing is the error this wording prevents.
      label: formatMore(overflow, "group"),
      cell: count(overflowSum),
      length: peak > 0 ? Math.min(overflowSum / peak, 1) : null,
    });
  }

  const remainder: DistRow | null =
    result.hiddenCount > 0
      ? {
          label: `${result.hiddenCount} ${
            result.hiddenCount === 1 ? "group" : "groups"
          } with fewer than ${K} ${options.unit}`,
          cell: result.hiddenSum,
          length:
            result.hiddenSum.kind === "value" && peak > 0
              ? Math.min(result.hiddenSum.value / peak, 1)
              : null,
          isRemainder: true,
        }
      : null;

  const provenance: Provenance =
    result.hiddenCount > 0 && options.studentLegend !== false
      ? {
          ...options.provenance,
          suppression: `${result.hiddenCount} ${
            result.hiddenCount === 1 ? "group is" : "groups are"
          } combined into one line.`,
        }
      : options.provenance;

  return {
    key: options.key,
    rows,
    remainder,
    hiddenCount: result.hiddenCount,
    total: result.total,
    allSuppressed: rows.every(
      (row) => row.cell.kind !== "value" || row.cell.value === 0,
    ),
    provenance,
    footnote: options.footnote,
    unit: options.unit,
  };
}

function tally(values: readonly string[]): { label: string; n: number }[] {
  const map = new Map<string, number>();
  for (const v of values) map.set(v, (map.get(v) ?? 0) + 1);
  return [...map].map(([label, n]) => ({ label, n }));
}

/** Every controlled value, including the ones nobody chose. A category missing
 *  from a chart and a category at zero are different claims. */
function tallyOver(
  vocabulary: readonly string[],
  values: readonly string[],
): { label: string; n: number }[] {
  const map = new Map<string, number>(vocabulary.map((v) => [v, 0]));
  for (const v of values) map.set(v, (map.get(v) ?? 0) + 1);
  return [...map].map(([label, n]) => ({ label, n }));
}

/**
 * The sample's cohort sizes, counted as the dashboard counts "Students in this
 * cohort": the students the default window holds in each (Sep 19 2026). The
 * sample's Enrollment lists eighteen of the institution's students, so its
 * Cohorts card takes these rather than counting its own rows, and the two
 * screens agree.
 */
export function demoCohortSizes(scenario: ScenarioId): Record<string, number> {
  const institution = institutionFor(scenario);
  const all = buildCohort(scenario);
  return Object.fromEntries(
    DEMO_COHORTS.map((c) => [
      c.id,
      applyFilters(all, { ...DEFAULT_FILTERS, cohort: c.id, mentorship: "any" }, institution).length,
    ]),
  );
}

/** The sample's claimed students in its default window: the hero's first figure. */
export function demoClaimedCount(scenario: ScenarioId): number {
  return applyFilters(buildCohort(scenario), DEFAULT_FILTERS, institutionFor(scenario)).length;
}

/* ==========================================================================
   Provenance

   Seven fields, one definition per metric, rendered by Trace in this order,
   and the same object is what an export's source appendix reads. A card whose
   metric cannot fill fields one through six does not render.
   ========================================================================== */

function provenance(p: Provenance): Provenance {
  return p;
}

/* ==========================================================================
   The derivation
   ========================================================================== */

export function deriveCohort(
  scenario: ScenarioId,
  requested: CohortFilters,
): CohortView {
  // A cohort never pairs with the mentorship filter (Sep 19 2026, review
  // item 4): the select is off under a cohort, and a value set before the
  // cohort was chosen, or carried in by a link, no longer narrows it. The
  // Worker refuses the pair for the same reason.
  const filters: CohortFilters = requested.cohort ? { ...requested, mentorship: "any" } : requested;
  const institution = institutionFor(scenario);
  const all = buildCohort(scenario);
  // The population the floor is judged on: the window's students, in the
  // selected cohort when one is, before the mentorship filter.
  const inWindow = applyFilters(all, { ...filters, mentorship: "any" }, institution);
  const view = applyFilters(all, filters, institution);
  const cohortLabel = filters.cohort ? DEMO_COHORTS.find((c) => c.id === filters.cohort)?.name : undefined;
  // The mentorship filter is the sample's slice (Sep 18 2026): the hero's
  // first figure is then the cut's count, and its label says which cut. A
  // cohort never pairs with a slice, the same rule the Worker keeps.
  const sliceLabel = !filters.cohort && filters.mentorship !== "any" ? SLICE_NAME[filters.mentorship] : undefined;

  const cohortSize = inWindow.length;
  const belowFloor = cohortSize < K;
  const label = windowLabel(filters.window, institution);
  const filtersActive = !filtersAreDefault(filters);

  const claimed = count(view.length);
  const matchedInView = view.filter((s) => s.mentorships > 0);
  const matchedCount = count(matchedInView.length);
  const matchedRate = rate(matchedInView.length, view.length);
  const mentorshipTotal = view.reduce((s, x) => s + x.mentorships, 0);
  const mentorshipsHeld = count(mentorshipTotal);

  // A ratio is only publishable when both of its parts are. Deriving it from a
  // suppressed count would republish that count at one decimal place.
  const perMatched: Cell<number> =
    matchedCount.kind === "value" &&
    mentorshipsHeld.kind === "value" &&
    matchedCount.value > 0
      ? value(Math.round((mentorshipTotal / matchedInView.length) * 10) / 10)
      : suppressed("complementary");

  const waiting = view.filter((s) => s.mentorships === 0);
  const withRequest = waiting.filter((s) => s.openRequest);

  const growth = buildGrowth(all, filters, institution);

  const seatClaimed = count(all.length);
  const seat: SeatMeter = {
    purchased: institution.seatsPurchased,
    claimed: seatClaimed,
    claimedRaw: all.length,
    available: count(Math.max(0, institution.seatsPurchased - all.length)),
    fraction:
      institution.seatsPurchased > 0
        ? Math.min(all.length / institution.seatsPurchased, 1)
        : 0,
    overCapacity: all.length > institution.seatsPurchased,
    renewsAt: institution.renewsAt,
    visibleToViewer: institution.viewerRole !== "Viewer",
  };

  const coverage: Coverage = {
    available: institution.enrollmentEstimate !== null,
    estimate: institution.enrollmentEstimate,
    claimed: seatClaimed,
    rate:
      institution.enrollmentEstimate !== null
        ? rate(all.length, institution.enrollmentEstimate)
        : { kind: "unavailable", reason: "no denominator" },
    provenance: provenance({
      definition:
        "The share of the students your team estimated who have connected a P3 account to your institution.",
      source: "From P3 student records over a figure your team gave P3.",
      window: label,
      denominator: "Over the enrollment estimate your team gave P3, shown in Settings.",
      freshness: FRESHNESS,
      limits: [
        "The numerator is exact. The denominator is yours, and P3 cannot check it.",
        "Students who have not named your institution are still using P3. Their activity is simply not attributed to you.",
      ],
    }),
  };

  return {
    scenario,
    institution,
    filters,
    filtersActive,
    windowLabel: label,
    cohortSize,
    belowFloor,
    claimed,
    claimedInView: view.length,
    claimedProvenance: provenance({
      definition:
        "A student is counted when they select your institution in the P3 app or use your join code.",
      source: "From P3 student records, not from app activity.",
      window: label,
      windowRule:
        "Weeks end on Sunday, and the current partial week is left out, so a part-week never reads as a collapse.",
      denominator:
        "Every student record naming your institution. There is no sampling: this is the whole population.",
      freshness: FRESHNESS,
      limits: [
        "This is a floor, not a register. A student who has not named your institution is invisible here even if they attend.",
        "A student can change the institution on their profile at any time, and their record moves with them.",
      ],
    }),
    growth,
    seat,
    coverage,
    channels: buildDistribution(
      tally(view.map((s) => s.channel)),
      {
        key: "channels",
        unit: "students",
        cap: 6,
        footnote:
          "Students who chose you in the app left no join event, so they have their own row.",
        provenance: provenance({
          definition: "How each student reached your roster.",
          source: "From P3 join events and student records.",
          window: label,
          denominator: "Every student in view. A student who chose you from the in-app list left no join event, so they have their own row.",
          freshness: FRESHNESS,
          limits: [
            "P3 cannot attribute a student who selected your institution from the in-app list to a campaign.",
          ],
        }),
      },
    ),
    matchedCount,
    matchedRate,
    mentorshipsHeld,
    perMatched,
    matchedProvenance: provenance({
      definition:
        "A student counts here when they hold at least one confirmed mentorship.",
      source: "From P3 student records, measured on each student's own record.",
      window: label,
      denominator:
        "Every student claimed to your institution in the selected window and filters.",
      freshness: FRESHNESS,
      limits: [
        "Never derived by subtracting mentor-side counts. A derived figure once read 427 where the truth was 550, because students hold more than one mentorship.",
        "Holding a mentorship is not the same as meeting regularly. P3 does not observe what happens in a mentorship.",
      ],
    }),
    waitingTotal: count(waiting.length),
    waitingWithRequest: count(withRequest.length),
    waitingWithoutRequest: count(waiting.length - withRequest.length),
    waitingRaw: waiting.length,
    waitingProvenance: provenance({
      definition:
        "Students with no confirmed mentorship, split by whether they have asked for one.",
      source: "From P3 student records.",
      window: label,
      denominator: "Every student in view with no confirmed mentorship.",
      freshness: FRESHNESS,
      limits: [
        "P3 does not tell you which students these are, and no export contains a student row.",
      ],
    }),
    mentorshipCounts: buildDistribution(
      [
        { label: "No mentor yet", n: waiting.length },
        { label: "One mentor", n: view.filter((s) => s.mentorships === 1).length },
        {
          label: "Two or more mentors",
          n: view.filter((s) => s.mentorships >= 2).length,
        },
      ],
      {
        key: "mentorship-counts",
        unit: "students",
        keepOrder: true,
        footnote:
          "Mentorships exceed matched students because some students have more than one mentor.",
        provenance: provenance({
          definition: "How many confirmed mentorships each student holds.",
          source: "From P3 student records.",
          window: label,
          denominator: "Every student in view.",
          freshness: FRESHNESS,
          limits: [
            "In a small cohort the two-or-more group can fall under the reporting threshold and roll up. That is the suppression rule working, not a gap in the data.",
          ],
        }),
      },
    ),
    supportAreas: buildDistribution(
      tallyOver(SUPPORT_AREAS, view.flatMap((s) => s.supportAreas)),
      {
        key: "support-areas",
        unit: "students",
        cap: 6,
        footnote:
          "Students pick one or more areas, so the rows sum to more than students.",
        provenance: provenance({
          definition:
            "The support areas your students selected on their P3 profile.",
          source: "From P3 student records.",
          window: label,
          denominator: "Every student in view, counted once per area they selected.",
          freshness: FRESHNESS,
          limits: [
            "This is what students say they want, not what they have received.",
          ],
        }),
      },
    ),
    industries: buildDistribution(
      tallyOver(INDUSTRIES, view.map((s) => s.industry)),
      {
        key: "industries",
        unit: "students",
        cap: 6,
        footnote:
          "One industry per student.",
        provenance: provenance({
          definition: "The industry each student named an interest in.",
          source: "From P3 student records.",
          window: label,
          denominator: "Every student in view who has set an industry.",
          freshness: FRESHNESS,
        }),
      },
    ),
    educationGoals: buildDistribution(
      tallyOver(EDUCATION_GOALS, view.map((s) => s.educationGoal)),
      {
        key: "education-goals",
        unit: "students",
        cap: 6,
        footnote: "Each student selects one education goal.",
        provenance: provenance({
          definition: "The education goal each student selected.",
          source: "From P3 student records.",
          window: label,
          denominator: "Every student in view who has set a goal.",
          freshness: FRESHNESS,
          limits: [
            "A goal is what a student intends. P3 holds no enrollment, credit or degree record and cannot tell you whether they got there.",
          ],
        }),
      },
    ),
    meetingRhythms: buildDistribution(
      tallyOver(MEETING_RHYTHMS, view.map((s) => s.meetingRhythm)),
      {
        key: "meeting-rhythms",
        unit: "students",
        keepOrder: true,
        footnote:
          "One rhythm per student: the mentor time your cohort expects.",
        provenance: provenance({
          definition: "The meeting rhythm each student asked for.",
          source: "From P3 student records.",
          window: label,
          denominator: "Every student in view.",
          freshness: FRESHNESS,
          limits: [
            "A stated preference, not an observed cadence. P3 does not record whether a meeting happened.",
          ],
        }),
      },
    ),
    activity: buildActivity(view, label),
    milestones: buildMilestones(view, filters, institution, label),
    timelines: buildTimelines(view, label),
    comparisonAvailable: {
      none: { ok: true },
      prev: institution.term.isFirst
        ? {
            ok: false,
            reason: `${institution.term.name} is your first term on P3, so there is no earlier term to compare with. This turns on at the start of ${institution.term.name === "Fall 2026" ? "Spring 2027" : "your next term"}.`,
          }
        : { ok: true },
      platform: { ok: true },
    },
    platform: {
      matched: platformMatchedRate,
      label: "Students with no institution",
      group: "students who have not named an institution",
      note: `Across P3, read on ${formatDate(PLATFORM.verifiedOn)}`,
      footnote: PLATFORM.exclusion,
    },
    asOf: AS_OF,
    freshnessLine: `${asOfStamp(AS_OF)} · Recomputed hourly.`,
    stale: false,
    held: false,
    ...(cohortLabel ? { cohortLabel } : {}),
    ...(sliceLabel ? { sliceLabel } : {}),
  };
}

/* ==========================================================================
   Growth

   Sunday-anchored, partial week excluded. This is not a preference. A partial
   current week renders as a cliff, every reader reads a cliff as a collapse,
   and it is enforced here in code rather than in a runbook.
   ========================================================================== */

function buildGrowth(
  students: readonly DemoStudent[],
  filters: CohortFilters,
  institution: Institution,
): GrowthSeries {
  return buildWeekly(
    students.map((s) => s.claimedAt),
    filters,
    institution,
    provenance({
      definition:
        "The running total of students claimed to your institution, by week.",
      source: "From P3 student records, by the date each student claimed you.",
      window: windowLabel(filters.window, institution),
      windowRule:
        "Weeks end on Sunday. The current partial week is not drawn, because a part-week reads as a collapse.",
      denominator:
        "Every student claimed to your institution. Filters other than the window do not apply to this curve.",
      freshness: FRESHNESS,
      limits: [
        "A week whose running total is under 10 students is not plotted. The curve starts at the first week that publishes.",
        "P3 began keeping dated snapshots when your subscription started, so the curve reaches back only that far.",
      ],
    }),
  );
}

/**
 * Any dated series, as a Sunday-anchored running total. The growth curve and
 * the milestone curve are the same drawing with different dates behind it.
 *
 * `publishable` is the caller's floor on the PEOPLE behind the dates. A count
 * of milestone completions is a count of events, and the suppression core
 * cannot see that twelve completions belong to four students. So the caller,
 * which can, withholds the whole curve when the group is under ten.
 */
function buildWeekly(
  dates: readonly string[],
  filters: CohortFilters,
  institution: Institution,
  seriesProvenance: Provenance,
  publishable = true,
): GrowthSeries {
  const lastComplete = previousSunday(AS_OF);
  const from = windowStart(filters.window, institution);
  const stamps = dates.map((d) => Date.parse(`${d}T12:00:00-05:00`));
  const earliest = stamps.reduce<number>(
    (min, t) => Math.min(min, t),
    Number.POSITIVE_INFINITY,
  );
  const start = Number.isFinite(earliest) ? Math.max(earliest, from) : lastComplete.getTime();

  const weeks: Date[] = [];
  const cursor = previousSunday(start);
  while (cursor.getTime() <= lastComplete.getTime() && weeks.length < 60) {
    weeks.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 7);
  }
  if (weeks.length === 0) weeks.push(lastComplete);

  const points: GrowthPoint[] = [];
  let previousCumulative = 0;
  for (const week of weeks) {
    const end = week.getTime() + DAY_MS - 1;
    const cumulative = stamps.filter((t) => t <= end).length;
    points.push({
      weekEnding: week.toISOString().slice(0, 10),
      // Formatted from the calendar date at midday Central, so the label cannot
      // slip a day when the reader's machine is in another zone.
      label: formatDate(`${week.toISOString().slice(0, 10)}T12:00:00-05:00`, {
        style: "short",
      }),
      cumulative: publishable ? count(cumulative) : suppressed("below-threshold"),
      added: publishable
        ? count(cumulative - previousCumulative)
        : suppressed("below-threshold"),
    });
    previousCumulative = cumulative;
  }

  const drawable = points.flatMap((p) =>
    p.cumulative.kind === "value"
      ? [{ label: p.label, value: p.cumulative.value }]
      : [],
  );

  return {
    points,
    drawable,
    suppressedWeeks: points.length - drawable.length,
    annotations: ANNOTATIONS.filter(
      (a) => Date.parse(`${a.date}T12:00:00-05:00`) >= from,
    ),
    provenance: seriesProvenance,
  };
}

/**
 * Annotation markers are placed by date across the drawn weeks, so a marker
 * that falls outside the plotted range is dropped rather than clamped to an
 * edge, where it would imply an event happened in a week it did not.
 */
export function annotationPositions(
  series: GrowthSeries,
): readonly { label: string; at: number }[] {
  const points = series.points;
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last || points.length < 2) return [];
  const start = Date.parse(`${first.weekEnding}T12:00:00-05:00`);
  const end = Date.parse(`${last.weekEnding}T12:00:00-05:00`);
  if (end <= start) return [];
  return series.annotations.flatMap((a) => {
    const at = (Date.parse(`${a.date}T12:00:00-05:00`) - start) / (end - start);
    return at >= 0 && at <= 1 ? [{ label: a.label, at }] : [];
  });
}

/**
 * Partner-written markers. Demonstration values here; in production these are
 * rows the partner creates, and they render on every time series on the page.
 */
export const ANNOTATIONS: readonly Annotation[] = [
  /* Empty since Sep 9 2026 (Thomas: no prominent card that is not usable in
     reality). The three sample markers claimed a "markers your team added"
     feature no screen offers; the curve keeps the mechanism for the day one
     does. */
];

/* ==========================================================================
   Activity and milestones, per student

   A SECOND generator, seeded off the scenario, so adding these fields did not
   move a single figure the cohort already published. One engagement draw
   shapes everything below: the student who opens many opportunities is also
   the student who asks a question, because activity clusters in real cohorts
   and independent draws spread it too evenly to be believed.

   Milestone names are weighted by their real 12-month frequency across the
   platform (the Data Matrix's own read, Sep 2026), so the demo's top
   milestones rank the way the platform ranks.
   ========================================================================== */

/* The internship weight is 21, not the platform frequency's 11 (Sep 9 2026):
   at 11 the sample reported it for six students and the funnel folded its
   last two stages behind the privacy floor, which showed a buyer a two-step
   funnel. Every other weight is the real frequency. */
const MILESTONE_WEIGHTS = [10, 13, 7, 22, 12, 7, 42, 39, 19, 21, 1] as const;
const PATHWAY_WEIGHTS = [26, 18, 17, 9, 12, 6, 7, 5, 4, 5, 4, 5, 3, 4, 4, 2, 3] as const;

interface StudentActivity {
  readonly adult: boolean;
  readonly milestones: readonly MilestoneCompletion[];
  readonly opportunitiesOpened: number;
  readonly opportunitiesApplied: number;
  readonly questionsAsked: number;
  readonly pulseSessions: number;
  readonly pathwaysExplored: readonly string[];
}

/**
 * How many of a sample cohort have landed a first full-time role.
 *
 * PLACEHOLDER, and chosen for what it demonstrates rather than measured from
 * anything (Thomas, Sep 12 2026: "this is a demo so we should be OK using
 * placeholder numbers to showcase the feature"). The real platform has ONE
 * student at this milestone, so a faithful sample shows the field match as
 * "Not available" forever and nobody can see the capability work.
 *
 * The size is set by the floor, not by taste. A published rate needs ten on
 * BOTH sides of the split, because rate() holds the complement as well as the
 * share: ten of fourteen in their own field also says four were not, and four
 * is a figure about four people. So roughly thirty comparable students, split
 * about two to one, is the smallest cohort that can show a real percentage.
 */
const FIRST_ROLE_SHARE = 0.22;

function activityFor(
  rng: () => number,
  claimedAt: string,
  rngFirstRole: () => number,
): StudentActivity {
  const adult = rng() < 0.64;
  const engagement = rng() ** 1.4;
  const opened = Math.round(engagement * 14 * (0.6 + rng() * 0.8));
  const applied =
    opened === 0
      ? 0
      : Math.min(opened, Math.round(opened * (0.12 + rng() * 0.36)));
  const questionsAsked =
    rng() < 0.12 + 0.4 * engagement ? 1 + Math.floor(rng() * 3) : 0;
  const pulseSessions = adult ? Math.round(engagement * rng() * 9) : 0;

  const pathwayCount = weighted(rng, [0, 1, 2, 3], [34, 36, 21, 9]);
  const pathways = new Set<string>();
  while (pathways.size < pathwayCount) {
    pathways.add(weighted(rng, PATHWAYS, PATHWAY_WEIGHTS));
  }

  const milestoneCount = weighted(rng, [0, 1, 2, 3, 4], [36, 30, 19, 10, 5]);
  const names = new Set<string>();
  while (names.size < milestoneCount) {
    names.add(weighted(rng, MILESTONES, MILESTONE_WEIGHTS));
  }
  const start = Date.parse(`${claimedAt}T12:00:00-05:00`);
  const end = Date.parse("2026-09-06T12:00:00-05:00");
  const span = Math.max(end - start, DAY_MS);
  const milestones = [...names].map((name) => ({
    name,
    at: new Date(start + rng() * span).toISOString().slice(0, 10),
  }));
  /* Its date comes from the first-role stream too. Drawing it from `rng`
     would advance rngActivity by one per student and move every figure after
     it, which is the whole thing this stream exists to avoid. */
  if (rngFirstRole() < FIRST_ROLE_SHARE && !names.has(FIRST_ROLE_MILESTONE)) {
    milestones.push({
      name: FIRST_ROLE_MILESTONE,
      at: new Date(start + rngFirstRole() * span).toISOString().slice(0, 10),
    });
  }

  return {
    adult,
    milestones,
    opportunitiesOpened: opened,
    opportunitiesApplied: applied,
    questionsAsked,
    pulseSessions,
    pathwaysExplored: [...pathways],
  };
}

/** The limits every record total carries (Sep 11 2026), on the sample and
 *  on a live tenant alike. */
export const RECORD_LIMITS: readonly string[] = [
  "A running total since the student joined P3, including before they named your institution.",
  "If a student changes the institution on their profile, their totals move with them.",
];

/** What "not yet active" counts, word for word on both hosts. */
export const NOT_YET_ACTIVE_DEFINITION =
  "Students with no question asked, mentor video played or saved, mentor followed, mentorship or open request on record.";

export const NOT_YET_ACTIVE_LIMITS: readonly string[] = [
  "Opportunities, Pulse AI, pathways and milestones are not part of this count yet, so a student active only there is counted here.",
  "P3 never tells you which students these are, and no export contains a student row.",
];

/** App use and questions over time, word for word on both hosts (Sep 11 2026). */
export const APP_USE_SPAN = `in the last ${ACTIVITY_WINDOW_DAYS} days`;
export const APP_USE_WINDOW = `The last ${ACTIVITY_WINDOW_DAYS} complete days, ending yesterday.`;
export const APP_USE_DEFINITION = `Students who opened the P3 app in the last ${ACTIVITY_WINDOW_DAYS} days, how many sessions each had, and how long a session lasted.`;
export const APP_USE_SOURCE =
  "From the app's analytics, joined to your institution by the name on each student's profile.";
export const APP_USE_LIMITS: readonly string[] = [
  "A student counts only when their analytics profile names your institution. Some profiles do not yet, so every figure here is a floor.",
  "A student who turned off analytics on their device is not counted.",
  "A session is any time in the app, not only time with a mentor.",
];
export const ANSWERS_DEFINITION = `Questions your students asked P3 mentors in the last ${ACTIVITY_WINDOW_DAYS} days, and the share of all their questions a mentor has answered.`;
export const ANSWERS_SOURCE =
  "From P3 question records: each question's date, and whether a mentor replied in writing or with a video.";
export const ANSWERS_LIMITS: readonly string[] = [
  "A question counts as answered once a mentor replies to it, in writing or with a video.",
  "Questions follow the student: one asked before they named your institution counts here, and moves with them if they change it.",
  "P3 shows you counts, never the text of a question.",
];

interface StudentRecords {
  readonly videoPlays: number;
  readonly videosSaved: number;
  readonly mentorsFollowed: number;
}

/**
 * The record totals for one sample student. A student with a mentor or an
 * open request is rarely idle; one with neither often has not started,
 * which is the shape a first cohort has.
 */
function recordsFor(rng: () => number, engaged: boolean): StudentRecords {
  if (rng() < (engaged ? 0.05 : 0.7)) {
    return { videoPlays: 0, videosSaved: 0, mentorsFollowed: 0 };
  }
  const videoPlays = rng() < 0.85 ? 1 + Math.floor(rng() ** 2 * 60) : 0;
  const videosSaved = videoPlays > 0 && rng() < 0.3 ? 1 + Math.floor(rng() * 4) : 0;
  const mentorsFollowed = rng() < 0.65 ? 1 + Math.floor(rng() * 3) : 0;
  return { videoPlays, videosSaved, mentorsFollowed };
}

/** The sample's "now" for its trailing windows: the stamp every sample figure carries. */
const SAMPLE_NOW = Date.parse(AS_OF);

interface StudentApp {
  readonly appSessions: number;
  readonly appSeconds: number;
  readonly questionLog: readonly { readonly at: string; readonly answered: boolean }[];
}

/**
 * App use in the last 90 days, and each question's date and answer, for one
 * sample student. An engaged student usually opened the app this term; one
 * with nothing on record sometimes did. A session runs three to twelve
 * minutes, and a mentor answers about three questions in five.
 */
function appFor(rng: () => number, claimedAt: string, questionsAsked: number, engaged: boolean): StudentApp {
  const active = rng() < (engaged ? 0.85 : 0.3);
  const appSessions = active ? 1 + Math.floor(rng() ** 1.6 * 24) : 0;
  const appSeconds = appSessions * Math.round(180 + rng() * 540);
  const start = Date.parse(`${claimedAt}T12:00:00-05:00`);
  const span = Math.max(SAMPLE_NOW - start, DAY_MS);
  const questionLog = Array.from({ length: questionsAsked }, () => ({
    at: new Date(start + rng() * span).toISOString().slice(0, 10),
    answered: rng() < 0.6,
  }));
  return { appSessions, appSeconds, questionLog };
}

function activityFigure(
  total: number,
  studentsRaw: number,
  denominator: number,
): ActivityFigure {
  // The Worker's own cells (src/lib/event-figure.ts): the total needs ten
  // students behind it, and the student count carries the complement rule,
  // so the sample never shows what a live tenant would not.
  return { ...eventFigure(total, studentsRaw, denominator), studentsRaw };
}

const sumOf = (
  students: readonly DemoStudent[],
  pick: (s: DemoStudent) => number,
): number => students.reduce((total, s) => total + pick(s), 0);

/**
 * What a live tenant cannot be shown yet, shown the same way on the sample
 * (Thomas, Sep 19 2026: the sample shows only what a live tenant sees). The
 * reason is the live view's own (live-view.ts NOT_MEASURED).
 */
const NOT_COUNTED_YET = "not measured per institution yet";
const notCountedFigure = (): ActivityFigure => ({
  total: unavailable(NOT_COUNTED_YET),
  students: unavailable(NOT_COUNTED_YET),
  studentsRaw: 0,
  share: unavailable(NOT_COUNTED_YET),
});

function buildActivity(view: readonly DemoStudent[], label: string): Activity {
  const appliers = view.filter((s) => s.opportunitiesApplied > 0);
  const askers = view.filter((s) => s.questionsAsked > 0);
  const adults = view.filter((s) => s.adult);
  const pulseUsers = adults.filter((s) => s.pulseSessions > 0);

  const activityProvenance = provenance({
    definition:
      "Opportunities your students applied to through the P3 app: scholarships, internships and jobs. In the app, opening the provider's application is the application.",
    source: "From in-app activity on P3, joined to student records.",
    window: label,
    denominator:
      "Every student in view who allows analytics. A student who opted out is not counted, so this is a floor.",
    freshness: FRESHNESS,
    limits: [
      "P3 knows a student applied through the app. It does not know the result, and it holds no record from the scholarship provider or the employer.",
      "Opening an opportunity without applying is not counted per institution yet.",
      "A student who turned off analytics on their device is not counted.",
    ],
  });

  const questionsProvenance = provenance({
    definition:
      "Questions your students have asked P3 mentors, directly or in the open question pool.",
    source: "From P3 student records: a running total on each student's record.",
    window: label,
    denominator: "Every student in view.",
    freshness: FRESHNESS,
    limits: [
      "Questions are asked in the student's own words. P3 shows you the count, never the text.",
      ...RECORD_LIMITS,
    ],
  });

  const pulseProvenance = provenance({
    definition:
      "Conversations your students had with Pulse AI, the career coach inside the app.",
    source: "From in-app activity on P3, joined to student records.",
    window: label,
    denominator:
      "Every student in view who is 18 or over. Pulse AI is not offered to younger students.",
    freshness: FRESHNESS,
    limits: [
      "Pulse AI and CV review are available to students 18 and over only.",
      "P3 does not read what a student asked. A session is counted; its content is not.",
    ],
  });

  const pathwaysProvenance = provenance({
    definition: "Career pathways your students opened in the app's guide.",
    source: "From in-app activity on P3, joined to student records.",
    window: label,
    denominator:
      "Every student in view. A student is counted once per pathway they opened.",
    freshness: FRESHNESS,
    limits: [
      "Seventeen pathways are available to every student. Opening one is interest, not a decision.",
    ],
  });

  // The record totals (Sep 11 2026), measured on a live tenant today, and
  // built here exactly as the Worker builds them.
  const watchers = view.filter((s) => s.videoPlays > 0);
  const savers = view.filter((s) => s.videosSaved > 0);
  const followers = view.filter((s) => s.mentorsFollowed > 0);
  const idle = view.filter(
    (s) =>
      s.videoPlays === 0 &&
      s.videosSaved === 0 &&
      s.mentorsFollowed === 0 &&
      s.questionsAsked === 0 &&
      s.mentorships === 0 &&
      !s.openRequest,
  );
  const idleShare = rate(idle.length, view.length);

  const videosProvenance = provenance({
    definition: "Mentor videos your students played and saved, and the mentors they follow.",
    source: "From P3 student records: running totals on each student's record.",
    window: label,
    denominator: "Every student in view.",
    freshness: FRESHNESS,
    limits: [...RECORD_LIMITS],
  });

  const notYetActiveProvenance = provenance({
    definition: NOT_YET_ACTIVE_DEFINITION,
    source: "From P3 student records.",
    window: label,
    denominator: "Every student in view.",
    freshness: FRESHNESS,
    limits: [...NOT_YET_ACTIVE_LIMITS],
  });

  // App use (Sep 11 2026), built exactly as the Worker builds it
  // (src/lib/app-use.ts): the students active in the last 90 days, their
  // sessions, and the applications behind them.
  const actives = view.filter((s) => s.appSessions > 0);
  const openers = view.filter((s) => s.opportunitiesOpened > 0);
  const sessions = sumOf(actives, (s) => s.appSessions);
  const appCells = activityCells(
    {
      activeStudents: actives.length,
      applyStudents: appliers.length,
      applications: sumOf(appliers, (s) => s.opportunitiesApplied),
      // Opens, counted per institution since Sep 19 2026, so the sample shows
      // them the way a live tenant's dashboard does.
      opens: sumOf(openers, (s) => s.opportunitiesOpened),
      openStudents: openers.length,
      sessionsPerStudent: actives.length > 0 ? sessions / actives.length : null,
      averageSessionSeconds: sessions > 0 ? sumOf(actives, (s) => s.appSeconds) / sessions : null,
    },
    activityWindow(SAMPLE_NOW),
    view.length,
    K,
  );
  const appUseProvenance = provenance({
    definition: APP_USE_DEFINITION,
    source: APP_USE_SOURCE,
    window: APP_USE_WINDOW,
    denominator: "Every student in view.",
    freshness: FRESHNESS,
    limits: [...APP_USE_LIMITS],
  });

  // Questions by week and answered (Sep 11 2026), built exactly as the
  // Worker builds them (src/lib/question-figures.ts).
  const questionLog: QuestionRecord[] = view.flatMap((s) =>
    s.questionLog.map((q, i) => ({
      id: `${s.id}-${i}`,
      student: s.id,
      at: Date.parse(`${q.at}T12:00:00-05:00`),
      answered: q.answered,
    })),
  );
  const asked = questionCells(view.map((s) => s.id), questionLog, K, SAMPLE_NOW);
  const today = Math.floor(SAMPLE_NOW / DAY_MS) * DAY_MS;
  const askedRaw = new Set(
    questionLog
      .filter((q) => q.at >= today - ACTIVITY_WINDOW_DAYS * DAY_MS && q.at < today)
      .map((q) => q.student),
  ).size;
  const answersProvenance = provenance({
    definition: ANSWERS_DEFINITION,
    source: ANSWERS_SOURCE,
    window: APP_USE_WINDOW,
    denominator: "Every question asked by a student in view.",
    freshness: FRESHNESS,
    limits: [...ANSWERS_LIMITS],
  });

  return {
    // Pathway views, the rate between opening and applying and the count of
    // students 18 and over are not counted per institution, so the sample
    // draws them exactly as a live tenant's screen does (Sep 19 2026).
    // Opens ARE counted from Sep 19 2026, so the sample shows a figure again.
    opportunitiesOpened: activityFigure(
      sumOf(openers, (s) => s.opportunitiesOpened),
      openers.length,
      view.length,
    ),
    opportunitiesApplied: activityFigure(
      sumOf(appliers, (s) => s.opportunitiesApplied),
      appliers.length,
      view.length,
    ),
    applyRate: unavailable(NOT_COUNTED_YET),
    questions: activityFigure(
      sumOf(askers, (s) => s.questionsAsked),
      askers.length,
      view.length,
    ),
    pulse: activityFigure(
      sumOf(pulseUsers, (s) => s.pulseSessions),
      pulseUsers.length,
      adults.length,
    ),
    adults: unavailable(NOT_COUNTED_YET),
    adultsRaw: 0,
    pathways: notCountedFigure(),
    pathwaysExplored: buildDistribution(
      [],
      {
        key: "pathways-explored",
        unit: "students",
        cap: 6,
        footnote:
          "Rows sum to views, not students.",
        provenance: pathwaysProvenance,
      },
    ),
    videoPlays: activityFigure(
      sumOf(watchers, (s) => s.videoPlays),
      watchers.length,
      view.length,
    ),
    videosSaved: activityFigure(
      sumOf(savers, (s) => s.videosSaved),
      savers.length,
      view.length,
    ),
    mentorsFollowed: activityFigure(
      sumOf(followers, (s) => s.mentorsFollowed),
      followers.length,
      view.length,
    ),
    notYetActive: idleShare.kind === "value" ? count(idle.length) : idleShare,
    notYetActiveShare: idleShare,
    notYetActiveRaw: idle.length,
    recordsMeasured: true,
    provenance: activityProvenance,
    questionsProvenance,
    pulseProvenance,
    pathwaysProvenance,
    videosProvenance,
    notYetActiveProvenance,
    appUse: {
      active: appCells.activeStudents,
      activeRaw: actives.length,
      activeShare: appCells.activeShare,
      sessionsPerStudent: appCells.sessionsPerStudent,
      averageSessionSeconds: appCells.averageSessionSeconds,
      span: APP_USE_SPAN,
      provenance: appUseProvenance,
    },
    answers: {
      asked: { ...asked.asked, studentsRaw: askedRaw },
      answered: asked.answered,
      weekly: asked.weekly.points,
      span: APP_USE_SPAN,
      provenance: answersProvenance,
    },
    // A live tenant's layout: applications without the opens, and pathway
    // views as the sentence a live tenant reads (Sep 19 2026).
    liveTenant: true,
    pathwaysNotMeasured: PATHWAYS_NOT_COUNTED,
  };
}

/** The milestone whose note carries the first role, as the reader names it. */
const FIRST_ROLE_MILESTONE = "Landed My First Full-Time Role";

/**
 * The sample's field match, through the REAL comparison.
 *
 * The sample cannot classify anything: classifying means reading a note, and
 * that happens only inside `p3-milestones-reader`. So it stands in for the
 * label the reader would have sent, and then runs `fieldMatchCounts` and
 * `rate` exactly as the Worker does, so the sample cannot show a figure the
 * live path could not produce.
 *
 * Deterministic by position, not random: every fourth student who landed a
 * role landed it outside their own field, which is a believable share and a
 * stable one across renders. The drift lands three along the industry list
 * and skips "Other", because "Other" is not comparable on either side.
 */
function sampleFieldMatch(
  view: readonly DemoStudent[],
  completions: readonly { readonly name: string; readonly student: string }[],
): Cell<Rate> {
  const landed = new Set(
    completions.filter((c) => c.name === FIRST_ROLE_MILESTONE).map((c) => c.student),
  );
  const comparablePool = FIELD_INDUSTRIES.filter((i) => i !== "Other");
  const pairs = view
    .filter((s) => landed.has(s.id))
    .map((s, i): { first: FirstRole | undefined; profileIndustry: unknown } => {
      const own = canonicalIndustry(s.industry);
      if (own === null || own === "Other") return { first: undefined, profileIndustry: s.industry };
      const elsewhere =
        comparablePool[(comparablePool.indexOf(own) + 3) % comparablePool.length] ?? own;
      /* Every THIRD, not every fourth. At a quarter out of field, thirty
         comparable students put seven or eight on the smaller side, under the
         floor, and rate() withholds the whole thing as `complementary`. Two
         to one clears ten on both sides and is a believable figure. */
      return {
        first: { role: i % 3 === 0 ? elsewhere : own, industry: null },
        profileIndustry: s.industry,
      };
    });
  const { matched, comparable } = fieldMatchCounts(pairs);
  return rate(matched, comparable);
}

function buildMilestones(
  view: readonly DemoStudent[],
  filters: CohortFilters,
  institution: Institution,
  label: string,
): Milestones {
  const from = windowStart(filters.window, institution);
  const completions = view.flatMap((s) =>
    s.milestones
      .filter((m) => Date.parse(`${m.at}T12:00:00-05:00`) >= from)
      .map((m) => ({ ...m, student: s.id })),
  );
  const completers = new Set(completions.map((c) => c.student));
  const publishable = completers.size >= K;
  const outcomeNames: readonly string[] = OUTCOME_MILESTONES;
  const outcomeCompletions = completions.filter((c) => outcomeNames.includes(c.name));
  const outcomeStudents = new Set(outcomeCompletions.map((c) => c.student));

  const milestoneProvenance = provenance({
    definition:
      "Milestones your students marked complete in the Mentee Milestone Framework.",
    source: "From P3 milestone records, reported by students in the app.",
    window: label,
    denominator:
      "Every student in view. A student is counted once per milestone they completed, in the week they marked it complete.",
    freshness: FRESHNESS,
    limits: [
      "A milestone is reported by the student. P3 labels it a signal, not a record, and does not verify it against a registrar or an employer.",
      "Only completions dated inside the window are counted, so a milestone a student completed before claiming you is not counted here.",
    ],
  });

  const outcomesProvenance = provenance({
    definition:
      "Outcome milestones your students reported: starting a program, declaring a major, completing an internship, landing a first full-time role.",
    source: "From P3 milestone records, reported by students in the app.",
    window: label,
    denominator:
      "Every student in view who reported at least one outcome milestone in the window.",
    freshness: FRESHNESS,
    limits: [
      "An outcome milestone asks the student to say where: which program, which major, which employer. That note stays private to the student and their mentor, and only the count reaches this page.",
      "Student reported and unverified. P3 holds no registrar, enrollment or employer record.",
    ],
  });

  return {
    completed:
      publishable || completers.size === 0
        ? count(completions.length)
        : suppressed("below-threshold"),
    completedRaw: completions.length,
    students: count(completers.size),
    studentsRaw: completers.size,
    rate: rate(completers.size, view.length),
    perStudent: publishable
      ? value(Math.round((completions.length / completers.size) * 10) / 10)
      : suppressed("complementary"),
    top: buildDistribution(tally(completions.map((c) => c.name)), {
      key: "top-milestones",
      unit: "students",
      cap: 6,
      footnote:
        "Rows sum to completions, not students.",
      provenance: milestoneProvenance,
    }),
    byWeek: buildWeekly(
      completions.map((c) => c.at),
      filters,
      institution,
      provenance({
        definition:
          "The running total of milestones your students marked complete, by week.",
        source: "From P3 milestone records, by the date each was marked complete.",
        window: label,
        windowRule:
          "Weeks end on Sunday. The current partial week is not drawn, because a part-week reads as a collapse.",
        denominator: "Every milestone completed by a student in view.",
        freshness: FRESHNESS,
        limits: [
          "The curve is withheld until ten students have completed a milestone, because a count of completions is a count of the people behind it.",
        ],
      }),
      publishable,
    ),
    outcomes: buildDistribution(
      // Stage order, and distinct students per stage: a student who reported
      // the same outcome twice is one student here.
      tally(
        [...new Set(outcomeCompletions.map((c) => `${c.name}\u0000${c.student}`))]
          .map((pair) => pair.split("\u0000")[0] ?? "")
          .sort((a, b) => outcomeNames.indexOf(a) - outcomeNames.indexOf(b)),
      ),
      {
        key: "outcomes",
        unit: "students",
        keepOrder: true,
        footnote: "In the order a student reaches them. Student reported when marked complete.",
        provenance: outcomesProvenance,
      },
    ),
    progress: buildDistribution(
      tally(
        view
          .map((s) => completions.filter((c) => c.student === s.id).length)
          .map((n) => (n === 0 ? "None" : n === 1 ? "One" : n === 2 ? "Two" : "Three or more"))
          .sort((a, b) => PROGRESS_ORDER.indexOf(a) - PROGRESS_ORDER.indexOf(b)),
      ),
      {
        key: "progress",
        unit: "students",
        keepOrder: true,
        footnote: "Every student in view, by milestones completed in the window.",
        provenance: milestoneProvenance,
      },
    ),
    outcomeStudents: count(outcomeStudents.size),
    outcomeStudentsRaw: outcomeStudents.size,
    fieldMatch: sampleFieldMatch(view, completions),
    provenance: milestoneProvenance,
    outcomesProvenance,
    // The same window `from` filtered the completions by, named (Sep 18 2026).
    span: windowSpan(filters.window, institution),
  };
}

/**
 * One kind-or-stage group of Career Timeline entries, under the web store's
 * rule so the sample cannot show a group the live path would withhold: the
 * group publishes only when the students keeping a timeline clear the floor
 * and no bucket stands for one to nine students. Each published row is a
 * count of entries.
 */
function timelineGroup<Key extends string>(
  view: readonly DemoStudent[],
  keys: readonly Key[],
  pick: (entry: TimelineEntry) => Key,
  labels: Readonly<Record<Key, string>>,
  options: DistOptions,
): Distribution {
  const keepers = view.filter((s) => s.timeline.length > 0);
  const rows = keys.map((key) => ({
    label: labels[key],
    entries: sumOf(view, (s) => s.timeline.filter((e) => pick(e) === key).length),
    students: view.filter((s) => s.timeline.some((e) => pick(e) === key)).length,
  }));
  const whole = keepers.length >= K && rows.every((r) => r.students === 0 || r.students >= K);
  return buildDistribution(whole ? rows.map((r) => ({ label: r.label, n: r.entries })) : [], options);
}

function buildTimelines(view: readonly DemoStudent[], label: string): CareerTimelines {
  const keepers = view.filter((s) => s.timeline.length > 0);
  const timelineProvenance = provenance({
    definition: TIMELINE_DEFINITION,
    source: "From the Career Timeline each student keeps in P3.",
    window: label,
    denominator: "Every student in view.",
    freshness: FRESHNESS,
    limits: TIMELINE_LIMITS,
  });
  const group = {
    unit: "entries",
    keepOrder: true,
    provenance: timelineProvenance,
  } as const;
  return {
    withTimeline: rate(keepers.length, view.length),
    studentsRaw: keepers.length,
    entries: activityFigure(sumOf(keepers, (s) => s.timeline.length), keepers.length, view.length),
    byKind: timelineGroup(view, TIMELINE_KINDS, (e) => e.kind, TIMELINE_KIND_LABELS, {
      ...group,
      key: "timeline-kind",
      footnote: "Entries, not students: a student with a school and a role is in both rows.",
    }),
    byStage: timelineGroup(view, TIMELINE_STAGES, (e) => e.stage, TIMELINE_STAGE_LABELS, {
      ...group,
      key: "timeline-stage",
      footnote: "Planned is a school or role the student intends, in their own words.",
    }),
    provenance: timelineProvenance,
  };
}

/* ==========================================================================
   Designed absences

   A panel that is missing without a reason reads as a panel that is broken.
   Each of these is named on the page, with the reason on its face. None of
   them is coming back as a greyed-out chart.
   ========================================================================== */

export interface AbsentPanel {
  readonly title: string;
  readonly status: "Cut" | "Not yet";
  readonly reason: string;
}

export const ABSENT_PANELS: readonly AbsentPanel[] = [
  /* REFRAMED Sep 9 2026, twice at Thomas's insistence. The old entry said P3
     holds no graduation or employment data at all, which undersold the
     product: students report graduation, a major, a first role and an
     employer in their milestones and career timeline, and the Milestones
     section shows them. What P3 does not hold is a VERIFIED record. */
  {
    title: "Verified graduation, employment and salary records",
    status: "Cut",
    reason:
      "Graduation, major, first role and employer appear on Milestones as students report them, and are labeled that way. P3 holds no registrar or employer record to verify them against, and no GPA, credit hours or salary.",
  },
  /* Likewise: P3 does compare, against all P3 students in aggregate, and the
     comparison control ships. What it does not have is a named peer or a
     third party dataset. */
  {
    title: "Named peer institutions",
    status: "Cut",
    reason:
      "Your cohort is compared against all P3 students in aggregate, in the header and on Mentorship. P3 holds no third party benchmark dataset, so a figure set against a named peer or a national average would be an assertion rather than a measurement.",
  },
  {
    title: "A custom report builder",
    status: "Cut",
    reason:
      "Reports you cannot build are worse than reports you can. The filters on this page are wired to real fields, and the export carries whatever they are set to.",
  },
  {
    title: "App activity by week for your institution",
    status: "Not yet",
    reason:
      "Students active in the app, their sessions and applications through the app are counted over the last 90 days as one figure, from the app's analytics. A week-by-week curve needs ten active students in every single week to publish, and no institution on P3 reaches that yet; it turns on when one does. Pulse AI sessions and pathway views are not yet counted per institution.",
  },
  {
    title: "Individual activity",
    status: "Cut",
    reason:
      "Enrollment lists each student by first name and last initial, with when they joined and whether they have a mentor. What a student does in P3 is never shown for one person: every activity figure covers ten or more students, and no export contains a student row. That is a commitment in your Data Sharing Addendum.",
  },
];

/** What P3 does not VERIFY, stated plainly, in the block a data governance
 *  officer reads first. Each line says what P3 holds instead, because the
 *  earlier version ("What P3 does not measure") read as if students'
 *  graduation, employer and scholarship activity were invisible to P3, and
 *  they are not: students report them, and the app records applications. */
export const NOT_VERIFIED: readonly string[] = [
  "Graduation and degree completion: reported by students as a milestone, never verified against a registrar.",
  "GPA, credit hours and enrollment status: not held. Affiliation is asserted by the student.",
  "Employer and job title: reported by students in a milestone note or their career timeline, never verified. Salary is not held.",
  "Credential and certificate awards: not held.",
  "Scholarship and job applications: P3 records that a student applied through the app, not the result.",
  "Attendance, advising notes and anything in your LMS: not held.",
];

export const SOURCE_TABLE: readonly {
  what: string;
  source: string;
  cadence: string;
  precision: string;
}[] = [
  {
    what: "Students claimed, mentorship counts, what students need",
    source: "P3 student records",
    cadence: "Hourly",
    precision: "Exact, whole population",
  },
  {
    what: "Questions asked, mentor videos played and saved, mentors followed",
    source: "P3 student records, running totals",
    cadence: "Hourly",
    precision: "Exact, whole population, including activity from before a student named you",
  },
  {
    what: "Weekly sign-ups",
    source: "P3 student records, by claim date",
    cadence: "Hourly",
    precision: "Exact, weeks end Sunday",
  },
  {
    what: "How students joined",
    source: "P3 join events",
    cadence: "On the event",
    precision: "Exact for join-code, link, poster and domain joins",
  },
  {
    what: "Roster coverage",
    source: "P3 student records over an enrollment figure your team gave P3",
    cadence: "Hourly",
    precision: "The numerator is exact, the denominator is yours",
  },
  {
    what: "Milestones and outcomes",
    source: "P3 milestone records, reported by students in the app",
    cadence: "Hourly",
    precision: "Exact count, student reported and unverified",
  },
  {
    what: "App use and applications through the app",
    source: "The app's analytics, joined to your institution by the name on each student's profile",
    cadence: "Hourly, over the last 90 days",
    precision: "A floor: only students whose analytics profile names you",
  },
  {
    what: "Questions answered, and questions by week",
    source: "P3 question records",
    cadence: "Hourly",
    precision: "Exact, whole population, including questions from before a student named you",
  },
  {
    what: "Pulse AI, pathways",
    source: "In-app activity on P3, joined to student records",
    cadence: "Daily",
    precision: "Exact for students who allow analytics, so a floor",
  },
];

/** The asymmetry a partner will ask about the first time a figure drops. */
export const KNOWN_LIMITS: readonly string[] = [
  "If a student changes the institution on their profile, their record moves with them, and so do their totals. P3 does not keep a figure attributed to an institution the student has left.",
  "Questions, mentor video plays, saves and follows are running totals on each student's record, so they include what a student did before naming your institution.",
  "Roster coverage compares P3's own count to an enrollment figure your team gave P3. P3 cannot see your enrollment records.",
  "P3 records when a student claims your institution, when they hold a mentorship, and what they say they need. It does not observe what happens inside a mentorship.",
  "A milestone is reported by the student in the app. P3 labels it a signal, not a record, and does not verify it against a registrar or an employer.",
  "Pulse AI is offered to students 18 and over, so a Pulse AI figure, where one is shown, covers adult students only. A student who turned off analytics on their device is not counted in any activity figure.",
  "App use and applications through the app come from the app's analytics, which carry your institution only for students whose analytics profile names it, so those figures are a floor.",
];


/**
 * Advice, and it reads as advice. The alternative sentence that was drafted
 * for this slot ("institutions that put the link in an orientation email see
 * coverage rise fastest") asserts a pattern P3 has never observed, and P3 has
 * zero paying partners, so there is no experience to describe. It is not
 * shipped until it is measured.
 */
export const CLAIM_ADVICE: readonly string[] = [
  "Put the link in an orientation email from a named advisor, not an office.",
  "Pin the poster where students already queue: advising, financial aid, a lobby.",
  "Give it thirty seconds at a first-year session, with the code on the slide.",
];

/* ==========================================================================
   The team

   Sample members for the Team and roles screen (Sep 9 2026, late). The
   Worker's rules are mirrored here so the sample behaves as the real thing
   will: an invitation goes only to a domain an active member already holds,
   a person is on the team once, and there is always an owner.
   ========================================================================== */

export interface TeamMember {
  readonly id: string;
  readonly name: string | null;
  readonly email: string;
  readonly role: TeamRole;
  readonly status: "Active" | "Invited";
  readonly lastSeen: string | null;
  /** The signed-in person. Cannot remove themselves or drop their own role. */
  readonly you?: boolean;
}

export function sampleTeam(institution: Institution): readonly TeamMember[] {
  const domain = institution.teamDomain;
  return [
    {
      id: "mem-1",
      name: institution.contactName,
      email: `dean.success@${domain}`,
      role: institution.viewerRole,
      status: "Active",
      lastSeen: AS_OF,
      you: true,
    },
    {
      id: "mem-2",
      name: "Priya Raman",
      email: `p.raman@${domain}`,
      role: "Admin",
      status: "Active",
      lastSeen: "2026-09-05T15:10:00-05:00",
    },
    {
      id: "mem-3",
      name: null,
      email: `m.bell@${domain}`,
      role: "Viewer",
      status: "Invited",
      lastSeen: null,
    },
  ];
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Why an invitation would be refused, in the Worker's own words, or null. */
export function inviteError(email: string, team: readonly TeamMember[]): string | null {
  const address = email.trim().toLowerCase();
  if (!EMAIL_SHAPE.test(address)) return "Enter a work email address.";
  const domain = address.slice(address.indexOf("@") + 1);
  const held = team.some((m) => m.status === "Active" && m.email.toLowerCase().endsWith(`@${domain}`));
  if (!held) return `Only addresses at a domain your team already uses here. Ask P3 to add ${domain}.`;
  if (team.some((m) => m.email.toLowerCase() === address)) return "That person is already on your team.";
  return null;
}
